import { useState, useEffect, useMemo } from 'react';
import { getTramitesART, getResumenTramitesART } from '../../services/art/tramiteARTService';
import { useNavigate } from 'react-router-dom';
import { getEstados } from '../../services/estadoService';
import {
    Snackbar, Alert, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../../components/mobile/BottomSheet';
import BotonFiltros from '../../components/mobile/BotonFiltros';
import ChipsFiltros from '../../components/mobile/ChipsFiltros';
import { formatCuil } from '../../utils/formatCuil';
import TarjetaKpi from '../../components/ui/TarjetaKpi';
import PageHeader from '../../components/ui/PageHeader';

// ── estados post-telegrama ────────────────────────────────────────────────────
const ESTADOS_POST_TELEGRAMA = new Set([
    'telegrama enviado', 'citación art', 'respuesta art',
    'iniciado en comisión médica', 'resolución srt', 'judicializado',
    'finalizado', 'cancelado',
]);
const esTelegramaEnviado = (nombreEstado) =>
    ESTADOS_POST_TELEGRAMA.has((nombreEstado || '').toLowerCase().trim());
// ─────────────────────────────────────────────────────────────────────────────

// ── colores por estado ────────────────────────────────────────────────────────
const COLORES_ESTADO = {
    'ingreso del trámite':          { bg: '#e0f2fe', color: '#0369a1' },
    'turno médico asignado':        { bg: '#fef3c7', color: '#92400e' },
    'espera de documentación':      { bg: '#fefce8', color: '#854d0e' },
    'enviados a la médica':         { bg: '#f3e8ff', color: '#7e22ce' },
    'preparando telegrama':         { bg: '#ccfbf1', color: '#0f766e' },
    'telegrama enviado':            { bg: '#dbeafe', color: '#1e40af' },
    'citación art':                 { bg: '#e0e7ff', color: '#3730a3' },
    'respuesta art':                { bg: '#d1fae5', color: '#065f46' },
    'iniciado en comisión médica':  { bg: '#fae8ff', color: '#86198f' },
    'resolución srt':               { bg: '#dcfce7', color: '#166534' },
    'judicializado':                { bg: '#fee2e2', color: '#991b1b' },
    'finalizado':                   { bg: '#f0fdf4', color: '#15803d' },
    'cancelado':                    { bg: '#f3f4f6', color: '#374151' },
};
const colorEstado = (nombre) => COLORES_ESTADO[(nombre || '').toLowerCase().trim()] || { bg: '#f3f4f6', color: '#374151' };
// ─────────────────────────────────────────────────────────────────────────────

// ── helpers de vencimiento (usados por el detalle de cada fila) ──────────────
const obtenerClaveVencimiento = (plazo) => {
    const meses = Number(plazo);
    if (plazo == null || !Number.isFinite(meses)) return 'sin-plazo';
    if (meses <= 0) return 'vencidos';
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() + Math.round(meses));
    return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
};

const obtenerNombreVencimiento = (clave) => {
    if (clave === 'vencidos') return 'Trámites vencidos';
    if (clave === 'sin-plazo') return 'Sin plazo definido';
    const [anio, mes] = clave.split('-');
    const nombre = new Date(Number(anio), Number(mes) - 1, 1)
        .toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    return `Vencen en ${nombre.charAt(0).toUpperCase() + nombre.slice(1)}`;
};
// ─────────────────────────────────────────────────────────────────────────────

// ── helpers de agrupación por mes de ingreso ─────────────────────────────────
const obtenerClaveIngreso = (fechaIngreso) => {
    if (!fechaIngreso) return 'sin-fecha';
    const d = new Date(fechaIngreso);
    if (isNaN(d.getTime())) return 'sin-fecha';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const obtenerNombreIngreso = (clave) => {
    if (clave === 'sin-fecha') return 'Sin fecha de ingreso';
    const [anio, mes] = clave.split('-');
    const nombre = new Date(Number(anio), Number(mes) - 1, 1)
        .toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    return `Ingresados en ${nombre.charAt(0).toUpperCase() + nombre.slice(1)}`;
};

const estiloGrupoIngreso = (clave) => {
    if (clave === 'sin-fecha') return { header: '#e5e7eb', texto: '#374151', borde: '#d1d5db' };
    return { header: '#1e40af', texto: '#ffffff', borde: '#1e40af' };
};
// ─────────────────────────────────────────────────────────────────────────────

function ListadoTramitesART() {
    const [tramites, setTramites] = useState([]);
    const [resumen, setResumen] = useState({ total: 0, enProceso: 0, finalizados: 0 });
    const [loading, setLoading] = useState(true);
    const [filtroIdCliente, setFiltroIdCliente] = useFiltroPersistente('tramitesART.filtroIdCliente', '');
    const [filtroNombreApellido, setFiltroNombreApellido] = useFiltroPersistente('tramitesART.filtroNombreApellido', '');
    const [filtroEstado, setFiltroEstado] = useFiltroPersistente('tramitesART.filtroEstado', '');
    const [filtroOrganismo, setFiltroOrganismo] = useFiltroPersistente('tramitesART.filtroOrganismo', '');
    const [estados, setEstados] = useState([]);
    const [gruposExpandidos, setGruposExpandidos] = useState({});
    const [alertasExpandidas, setAlertasExpandidas] = useState(true);
    const [vencidosExpandidos, setVencidosExpandidos] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const navigate = useNavigate();

    const formatearCUIL = (cuil) => {
        if (!cuil || cuil === 'N/A') return 'N/A';
        const n = cuil.replace(/\D/g, '');
        if (n.length !== 11) return cuil;
        return `${n.slice(0, 2)}-${n.slice(2, 10)}-${n.slice(10)}`;
    };

    const obtenerUltimoComentario = (texto) => {
        if (!texto) return 'Sin comentarios';
        const lineas = texto.split('\n').filter(linea => linea.trim() !== '');
        return lineas.length > 0 ? lineas[lineas.length - 1] : 'Sin comentarios';
    };

    const cargarTramites = async () => {
        setLoading(true);
        try {
            const data = await getTramitesART(
                0, 1000,
                filtroIdCliente?.trim() || '',
                filtroEstado || null,
                filtroNombreApellido?.trim() || '',
                filtroOrganismo || null
            );
            const lista = data.content || [];
            setTramites(lista);

            // Expandir por defecto todos los grupos de ingreso
            const inicial = {};
            lista.forEach(t => {
                const k = obtenerClaveIngreso(t.fechaIngreso);
                if (!(k in inicial)) inicial[k] = true;
            });
            setGruposExpandidos(prev => ({ ...inicial, ...prev }));
        } catch (e) {
            console.error('Error al cargar trámites ART:', e);
            setSnackbar({ open: true, message: 'Error al cargar trámites ART', severity: 'error' });
        }
        setLoading(false);
    };

    const cargarResumen = async () => {
        try {
            const data = await getResumenTramitesART(
                filtroIdCliente?.trim() || '',
                filtroEstado || null,
                filtroNombreApellido?.trim() || '',
                filtroOrganismo || null
            );
            setResumen({
                total: data?.total || 0,
                enProceso: data?.enProceso || 0,
                finalizados: data?.finalizados || 0,
            });
        } catch (e) {
            console.error('Error al cargar resumen ART:', e);
        }
    };

    const cargarEstados = async () => {
        try {
            const todos = await getEstados();
            setEstados((todos || []).filter(e => e.ambito === 'ART' || (e.nombre || '').toLowerCase() === 'cancelado'));
        } catch (e) {
            console.error('Error al cargar estados:', e);
        }
    };

    useEffect(() => { cargarEstados(); }, []);
    useEffect(() => {
        cargarTramites();
        cargarResumen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroIdCliente, filtroEstado, filtroNombreApellido, filtroOrganismo]);

    const parsearFechaEnvioTelegrama = (comentario) => {
        const match = (comentario || '').match(/[Ff]echa\s+de\s+env\S*o:\s*(\d{4}-\d{2}-\d{2})/);
        if (!match) return null;
        const fecha = new Date(match[1] + 'T00:00:00');
        return isNaN(fecha.getTime()) ? null : fecha;
    };

    const alertasTelegrama = useMemo(() => {
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        return tramites
            .filter(t => {
                if ((t.nombreEstadoActual || '').toLowerCase() !== 'telegrama enviado') return false;
                const fechaEnvio = parsearFechaEnvioTelegrama(t.ultimoComentario);
                if (!fechaEnvio) return false;
                const meses = (hoy.getFullYear() - fechaEnvio.getFullYear()) * 12 + (hoy.getMonth() - fechaEnvio.getMonth());
                return meses >= 3;
            })
            .map(t => {
                const fechaEnvio = parsearFechaEnvioTelegrama(t.ultimoComentario);
                const meses = (hoy.getFullYear() - fechaEnvio.getFullYear()) * 12 + (hoy.getMonth() - fechaEnvio.getMonth());
                const nombre = [t.apellidoCliente, t.nombreCliente].filter(Boolean).join(', ');
                return { ...t, mesesSinRespuesta: meses, nombreCompleto: nombre || 'Sin nombre' };
            })
            .sort((a, b) => b.mesesSinRespuesta - a.mesesSinRespuesta);
    }, [tramites]);

    const tramitesVencidos = useMemo(() => {
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        return tramites.filter(t => {
            if (esTelegramaEnviado(t.nombreEstadoActual)) return false;
            const estado = (t.nombreEstadoActual || '').toLowerCase();
            if (estado === 'finalizado' || estado === 'cancelado') return false;
            if (t.fechaVencimiento && typeof t.fechaVencimiento === 'string') {
                const venc = new Date(t.fechaVencimiento); venc.setHours(0, 0, 0, 0);
                if (!isNaN(venc.getTime())) return venc < hoy;
            }
            const meses = Number(t.plazo);
            return Number.isFinite(meses) && meses <= 0;
        });
    }, [tramites]);

    const tramitesAgrupados = useMemo(() => {
        const vencidosIds = new Set(tramitesVencidos.map(t => t.idTramite));
        const grupos = {};
        tramites.filter(t => !vencidosIds.has(t.idTramite)).forEach(t => {
            const clave = obtenerClaveIngreso(t.fechaIngreso);
            if (!grupos[clave]) grupos[clave] = [];
            grupos[clave].push(t);
        });

        // Ordenar dentro de cada grupo: menor plazo primero (más urgente arriba)
        Object.values(grupos).forEach(lista =>
            lista.sort((a, b) => (Number(a.plazo) || 0) - (Number(b.plazo) || 0))
        );

        // Ordenar claves: YYYY-MM asc (más antiguo primero) → sin-fecha al final
        const claves = Object.keys(grupos).sort((a, b) => {
            if (a === 'sin-fecha') return 1;
            if (b === 'sin-fecha') return -1;
            return a.localeCompare(b);
        });

        const ordenado = {};
        claves.forEach(k => { ordenado[k] = grupos[k]; });
        return ordenado;
    }, [tramites, tramitesVencidos]);

    const toggleGrupo = (clave) =>
        setGruposExpandidos(prev => ({ ...prev, [clave]: !prev[clave] }));

    const editarTramite = (id) => navigate(`/art/tramites/${id}`);


    const obtenerSemaforoPlazo = (plazo, nombreEstadoActual, fechaVencimiento) => {
        if (esTelegramaEnviado(nombreEstadoActual)) {
            return { chipBg: '#0891b2', chipColor: '#ffffff', chipText: 'Trámite Iniciado' };
        }
        if (fechaVencimiento && typeof fechaVencimiento === 'string') {
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            const venc = new Date(fechaVencimiento);
            venc.setHours(0, 0, 0, 0);
            if (isNaN(venc.getTime())) {
                const meses = Number(plazo);
                if (!Number.isFinite(meses)) return { chipBg: '#e5e7eb', chipColor: '#374151', chipText: 'Sin plazo' };
                if (meses <= 0)  return { chipBg: '#374151', chipColor: '#ffffff', chipText: 'Vencido' };
                if (meses <= 6)  return { chipBg: '#dc2626', chipColor: '#ffffff', chipText: 'Crítico' };
                if (meses <= 12) return { chipBg: '#f59e0b', chipColor: '#1f2937', chipText: 'Atención' };
                return { chipBg: '#16a34a', chipColor: '#ffffff', chipText: 'En plazo' };
            }
            const dias = Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
            if (dias < 0)   return { chipBg: '#374151', chipColor: '#ffffff', chipText: 'Vencido' };
            if (dias <= 180) return { chipBg: '#dc2626', chipColor: '#ffffff', chipText: 'Crítico' };
            if (dias <= 365) return { chipBg: '#f59e0b', chipColor: '#1f2937', chipText: 'Atención' };
            return { chipBg: '#16a34a', chipColor: '#ffffff', chipText: 'En plazo' };
        }
        const meses = Number(plazo);
        if (!Number.isFinite(meses)) return { chipBg: '#e5e7eb', chipColor: '#374151', chipText: 'Sin plazo' };
        if (meses <= 0)  return { chipBg: '#374151', chipColor: '#ffffff', chipText: 'Vencido' };
        if (meses <= 6)  return { chipBg: '#dc2626', chipColor: '#ffffff', chipText: 'Crítico' };
        if (meses <= 12) return { chipBg: '#f59e0b', chipColor: '#1f2937', chipText: 'Atención' };
        return { chipBg: '#16a34a', chipColor: '#ffffff', chipText: 'En plazo' };
    };

    return (
        <div className="area-art">
            <div className="container-fluid mt-4">
                <PageHeader
                    variante="plano"
                    icono="heart-pulse-fill"
                    titulo="Trámites ART"
                    descripcion="Gestión y seguimiento de trámites de ART — agrupados por mes de ingreso"
                    accion={
                        <button
                            className="btn d-flex align-items-center gap-2"
                            style={{ background: 'var(--area-solid)', color: '#fff', borderColor: 'var(--area-solid)' }}
                            onClick={() => navigate('/art/tramites/iniciar-tramite')}
                        >
                            <i className="bi bi-plus-circle-fill" aria-hidden="true" />
                            Iniciar Trámite ART
                        </button>
                    }
                />

                {/* Estadísticas */}
                <div className="row g-3 mb-4">
                    {[
                        { label: 'Total trámites', valor: resumen.total,       icono: 'folder2-open' },
                        { label: 'En proceso',     valor: resumen.enProceso,   icono: 'hourglass-split', tono: 'advertencia' },
                        { label: 'Finalizados',    valor: resumen.finalizados, icono: 'check-circle-fill', tono: 'exito' },
                    ].map((kpi) => (
                        <div className="col-md-4" key={kpi.label}>
                            <TarjetaKpi {...kpi} />
                        </div>
                    ))}
                </div>

                {/* Filtros */}
                {(() => {
                    const campoCuil = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-search me-2"></i>Buscar por CUIL
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-card-text" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input type="text" className="form-control" placeholder="Ingrese CUIL del cliente"
                                    value={filtroIdCliente}
                                    maxLength="13"
                                    onChange={e => { setFiltroIdCliente(formatCuil(e.target.value)); }}
                                    style={{ borderLeft: 'none' }} />
                            </div>
                        </>
                    );
                    const campoNombre = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-person me-2"></i>Buscar por Nombre/Apellido
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: '#6B7280' }}></i>
                                </span>
                                <input type="text" className="form-control" placeholder="Nombre o apellido del cliente"
                                    value={filtroNombreApellido}
                                    onChange={e => { setFiltroNombreApellido(e.target.value); }}
                                    style={{ borderLeft: 'none' }} />
                            </div>
                        </>
                    );
                    const campoEstado = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-list-check me-2"></i>Estado del Trámite
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-flag" style={{ color: '#6B7280' }}></i>
                                </span>
                                <select className="form-select" value={filtroEstado}
                                    onChange={e => { setFiltroEstado(e.target.value); }}
                                    style={{ borderLeft: 'none' }}>
                                    <option value="">Todos los estados</option>
                                    {estados.map(estado => (
                                        <option key={estado.id} value={estado.id}>{estado.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    );
                    const campoOrganismo = (
                        <>
                            <label className="form-label" >
                                <i className="bi bi-building me-2"></i>Organismo
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-bank" style={{ color: '#6B7280' }}></i>
                                </span>
                                <select className="form-select" value={filtroOrganismo}
                                    onChange={e => { setFiltroOrganismo(e.target.value); }}
                                    style={{ borderLeft: 'none' }}>
                                    <option value="">Todos</option>
                                    <option value="PROVINCIA">Provincia</option>
                                    <option value="ANSES">ANSES</option>
                                </select>
                            </div>
                        </>
                    );

                    const limpiarFiltros = () => {
                        setFiltroIdCliente('');
                        setFiltroNombreApellido('');
                        setFiltroEstado('');
                        setFiltroOrganismo('');
                    };

                    const chipsFiltros = [
                        filtroIdCliente && { key: 'cuil', label: `CUIL: ${filtroIdCliente}`, onQuitar: () => setFiltroIdCliente('') },
                        filtroNombreApellido && { key: 'nombre', label: filtroNombreApellido, onQuitar: () => setFiltroNombreApellido('') },
                        filtroEstado && {
                            key: 'estado',
                            label: estados.find(e => String(e.id) === String(filtroEstado))?.nombre || 'Estado',
                            onQuitar: () => setFiltroEstado(''),
                        },
                        filtroOrganismo && { key: 'organismo', label: filtroOrganismo === 'PROVINCIA' ? 'Provincia' : 'ANSES', onQuitar: () => setFiltroOrganismo('') },
                    ].filter(Boolean);

                    return esMobile ? (
                        <div className="mb-4">
                            <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                                >
                                    <div className="mb-3">{campoCuil}</div>
                                    <div className="mb-3">{campoNombre}</div>
                                    <div className="mb-3">{campoEstado}</div>
                                    <div>{campoOrganismo}</div>
                                </BottomSheet>
                            )}
                        </div>
                    ) : (
                        <div className="card shadow-sm mb-4" style={{ border: 'none', borderRadius: '12px' }}>
                            <div className="card-body" style={{ padding: '1.5rem' }}>
                                <div className="row g-3">
                                    <div className="col-lg-4 col-md-6">{campoCuil}</div>
                                    <div className="col-lg-4 col-md-6">{campoNombre}</div>
                                    <div className="col-lg-4 col-md-6">{campoEstado}</div>
                                    <div className="col-lg-4 col-md-6">{campoOrganismo}</div>
                                </div>
                                <div className="text-end mt-3">
                                    <button className="btn btn-outline-secondary"
                                        onClick={limpiarFiltros}
                                        style={{ borderRadius: '8px', padding: '0.5rem 1.5rem', fontWeight: '500' }}>
                                        <i className="bi bi-arrow-clockwise me-2"></i>Limpiar filtros
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Alertas: telegramas sin respuesta hace 3+ meses — colapsable */}
                {!loading && alertasTelegrama.length > 0 && (
                    <div className="card shadow-sm mb-4" style={{ border: '2px solid #fca5a5', borderRadius: '12px', overflow: 'hidden' }}>
                        <button
                            type="button"
                            className="d-flex align-items-center gap-2 w-100 px-4 py-3"
                            onClick={() => setAlertasExpandidas(p => !p)}
                            style={{ background: '#7f1d1d', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        >
                            <i className="bi bi-exclamation-triangle-fill text-white fs-5"></i>
                            <span style={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', flexGrow: 1 }}>
                                Telegramas sin respuesta de la ART — más de 3 meses
                            </span>
                            <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', borderRadius: '20px', padding: '2px 12px', fontSize: '0.82rem', fontWeight: 600, marginRight: '0.5rem' }}>
                                {alertasTelegrama.length} trámite{alertasTelegrama.length !== 1 ? 's' : ''}
                            </span>
                            <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {alertasExpandidas ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </span>
                        </button>
                        {alertasExpandidas && (
                            <div style={{ background: '#fef2f2', padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {alertasTelegrama.map(t => (
                                    <div key={t.idTramite}
                                        className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                                        style={{ background: 'white', border: '1px solid #fecaca', cursor: 'pointer' }}
                                        onClick={() => navigate(`/art/tramites/${t.idTramite}`)}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <i className="bi bi-clock-history" style={{ color: '#dc2626', fontSize: '1rem' }}></i>
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.9rem' }}>
                                                    {t.nombreCompleto}
                                                </div>
                                                <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>
                                                    Hace {t.mesesSinRespuesta} mes{t.mesesSinRespuesta !== 1 ? 'es' : ''} que se envió el telegrama y sigue sin respuesta de la ART
                                                </div>
                                            </div>
                                        </div>
                                        <span className="badge" style={{ background: '#dc2626', color: 'white', fontSize: '0.78rem', padding: '0.35rem 0.75rem' }}>
                                            {t.mesesSinRespuesta} mes{t.mesesSinRespuesta !== 1 ? 'es' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Sección de trámites vencidos — colapsable */}
                {!loading && tramitesVencidos.length > 0 && (
                    <div className="card shadow-sm mb-4" style={{ border: '2px solid #fca5a5', borderRadius: '12px', overflow: 'hidden' }}>
                        <button
                            type="button"
                            className="d-flex align-items-center justify-content-between w-100 px-4 py-3"
                            onClick={() => setVencidosExpandidos(p => !p)}
                            style={{ background: '#374151', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <i className="bi bi-hourglass-bottom text-white fs-5"></i>
                                <span style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                                    Trámites vencidos — plazo de 2 años superado
                                </span>
                                <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '20px', padding: '2px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                                    {tramitesVencidos.length} trámite{tramitesVencidos.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {vencidosExpandidos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </span>
                        </button>
                        {vencidosExpandidos && (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0 tabla-listado-ajustada">
                                    <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                        <tr>
                                            <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>CUIL</th>
                                            <th style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Cliente</th>
                                            <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Estado</th>
                                            <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Fecha estado</th>
                                            <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Vencimiento</th>
                                            <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tramitesVencidos.map(tramite => {
                                            const col = colorEstado(tramite.nombreEstadoActual);
                                            const vencText = (() => {
                                                if (tramite.fechaVencimiento) {
                                                    const hoy = new Date(); hoy.setHours(0,0,0,0);
                                                    const venc = new Date(tramite.fechaVencimiento); venc.setHours(0,0,0,0);
                                                    if (!isNaN(venc.getTime())) {
                                                        const dias = Math.round((venc - hoy) / (1000*60*60*24));
                                                        const fechaStr = venc.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                                        return `Venció el ${fechaStr} (hace ${Math.abs(dias)} día${Math.abs(dias)!==1?'s':''})`;
                                                    }
                                                }
                                                return tramite.plazo != null ? `${tramite.plazo} meses` : '—';
                                            })();
                                            return (
                                                <tr key={tramite.idTramite} style={{ borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                                                    <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                        <span className="badge" style={{ background: '#eff6ff', color: '#1e40af', padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                            {formatearCUIL(tramite.cuilCliente)}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '0.85rem 1rem', fontWeight: 500, color: '#374151' }}>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            <span>{[tramite.apellidoCliente, tramite.nombreCliente].filter(Boolean).join(', ') || 'N/A'}</span>
                                                            {tramite.organismo === 'ANSES' && (
                                                                <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>ANSES</span>
                                                            )}
                                                            {tramite.organismo === 'PROVINCIA' && (
                                                                <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Provincia</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                        <span className="badge" style={{ background: col.bg, color: col.color, padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500 }}>
                                                            {tramite.nombreEstadoActual || 'Sin estado'}
                                                        </span>
                                                    </td>
                                                    <td className="text-center" style={{ padding: '0.85rem 1rem', color: '#6B7280', fontSize: '0.875rem' }}>
                                                        {tramite.fechaEstadoActual ? new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES') : '—'}
                                                    </td>
                                                    <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                        <span style={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: 600 }}>{vencText}</span>
                                                    </td>
                                                    <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                        <button className="btn btn-primary btn-sm"
                                                            onClick={() => navigate(`/art/tramites/${tramite.idTramite}`)}
                                                            style={{ borderRadius: '6px', fontWeight: 500, padding: '0.35rem 0.85rem', fontSize: '0.85rem' }}>
                                                            <i className="bi bi-eye me-1"></i>Ver
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Controles de expansión */}
                {!loading && Object.keys(tramitesAgrupados).length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-semibold m-0" style={{ color: '#374151', fontSize: '1rem' }}>
                            <i className="bi bi-calendar3 me-2"></i>Trámites agrupados por mes de ingreso
                        </h5>
                        <button
                            type="button"
                            className="btn d-flex align-items-center gap-2"
                            onClick={() => {
                                const claves = Object.keys(tramitesAgrupados);
                                const todosExpandidos = claves.every(k => gruposExpandidos[k] ?? true);
                                const nuevo = {};
                                claves.forEach(k => { nuevo[k] = !todosExpandidos; });
                                setGruposExpandidos(nuevo);
                            }}
                            style={{
                                background: 'white',
                                color: '#6b7280',
                                border: '2px solid #e5e7eb',
                                borderRadius: '10px',
                                padding: '0.5rem 1rem',
                                fontWeight: 500,
                                fontSize: '0.875rem',
                            }}
                        >
                            {Object.keys(tramitesAgrupados).every(k => gruposExpandidos[k] ?? true)
                                ? <><ExpandLessIcon fontSize="small" />Colapsar todos</>
                                : <><ExpandMoreIcon fontSize="small" />Expandir todos</>
                            }
                        </button>
                    </div>
                )}

                {/* Contenido agrupado */}
                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                        <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámites ART...</p>
                    </div>
                ) : Object.keys(tramitesAgrupados).length === 0 ? (
                    <div className="card shadow-sm text-center py-5" style={{ border: 'none', borderRadius: '12px' }}>
                        <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                        <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No se encontraron trámites ART.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {Object.entries(tramitesAgrupados).map(([clave, lista]) => {
                            const estilo = estiloGrupoIngreso(clave);
                            const expandido = gruposExpandidos[clave] ?? true;

                            return (
                                <div key={clave} className="card shadow-sm" style={{ border: `1.5px solid ${estilo.borde}`, borderRadius: '12px', overflow: 'hidden' }}>
                                    {/* Cabecera del grupo */}
                                    <button
                                        type="button"
                                        className="d-flex align-items-center justify-content-between w-100 px-4 py-3"
                                        onClick={() => toggleGrupo(clave)}
                                        style={{
                                            background: estilo.header,
                                            border: 'none',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <span style={{ color: estilo.texto, fontWeight: '700', fontSize: '1rem' }}>
                                                {obtenerNombreIngreso(clave)}
                                            </span>
                                            <span style={{
                                                background: 'rgba(255,255,255,0.25)',
                                                color: estilo.texto,
                                                borderRadius: '20px',
                                                padding: '2px 12px',
                                                fontSize: '0.82rem',
                                                fontWeight: '600',
                                            }}>
                                                {lista.length} trámite{lista.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <span style={{ color: estilo.texto, opacity: 0.85 }}>
                                            {expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </span>
                                    </button>

                                    {/* Filas del grupo */}
                                    {expandido && (
                                        <div className="table-responsive">
                                            <table className="table align-middle mb-0 tabla-listado-ajustada">
                                                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                                    <tr>
                                                        <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>CUIL</th>
                                                        <th style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Cliente</th>
                                                        <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Estado</th>
                                                        <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Fecha estado</th>
                                                        <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Plazo / Vencimiento</th>
                                                        <th className="col-comentarios" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem', minWidth: '100px' }}>Comentarios</th>
                                                        <th className="text-center" style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#374151', fontSize: '0.85rem' }}>Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lista.map(tramite => {
                                                        const semaforo = obtenerSemaforoPlazo(tramite.plazo, tramite.nombreEstadoActual, tramite.fechaVencimiento);
                                                        const yaIniciado = esTelegramaEnviado(tramite.nombreEstadoActual);

                                                        const textoVencimiento = (() => {
                                                            if (yaIniciado) return null;
                                                            if (tramite.fechaVencimiento && typeof tramite.fechaVencimiento === 'string') {
                                                                const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
                                                                const venc = new Date(tramite.fechaVencimiento); venc.setHours(0, 0, 0, 0);
                                                                if (isNaN(venc.getTime())) return null;
                                                                const dias = Math.round((venc - hoy) / (1000 * 60 * 60 * 24));
                                                                const fechaStr = venc.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                                                if (dias < 0) return `Venció el ${fechaStr}`;
                                                                if (dias === 0) return 'Vence hoy';
                                                                if (dias <= 60) return `Vence en ${dias} día${dias !== 1 ? 's' : ''} (${fechaStr})`;
                                                                return `Vence el ${fechaStr}`;
                                                            }
                                                            if (tramite.plazo != null) {
                                                                const mesVenc = obtenerNombreVencimiento(obtenerClaveVencimiento(tramite.plazo));
                                                                return `${tramite.plazo} meses · ${mesVenc.replace('Vencen en ', '')}`;
                                                            }
                                                            return null;
                                                        })();

                                                        return (
                                                            <tr key={tramite.idTramite} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                                <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                                    <span className="badge" style={{ background: '#eff6ff', color: '#1e40af', padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                                        {formatCuil(tramite.cuilCliente)}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.85rem 1rem', fontWeight: '500', color: '#374151' }}>
                                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                        <span>{[tramite.apellidoCliente, tramite.nombreCliente].filter(Boolean).join(', ') || 'N/A'}</span>
                                                                        {tramite.organismo === 'ANSES' && (
                                                                            <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>ANSES</span>
                                                                        )}
                                                                        {tramite.organismo === 'PROVINCIA' && (
                                                                            <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '2px 8px', fontSize: '0.70rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Provincia</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                                    {(() => { const col = colorEstado(tramite.nombreEstadoActual); return (
                                                                        <span className="badge" style={{ background: col.bg, color: col.color, padding: '0.4rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500 }}>
                                                                            {tramite.nombreEstadoActual || 'Sin estado'}
                                                                        </span>
                                                                    ); })()}
                                                                </td>
                                                                <td className="text-center" style={{ padding: '0.85rem 1rem', color: '#6B7280', fontSize: '0.875rem' }}>
                                                                    {tramite.fechaEstadoActual ? new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES') : '—'}
                                                                </td>
                                                                <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                                    <Chip
                                                                        label={semaforo.chipText}
                                                                        size="small"
                                                                        sx={{ backgroundColor: semaforo.chipBg, color: semaforo.chipColor, fontWeight: 700, mb: 0.5 }}
                                                                    />
                                                                    {textoVencimiento && (
                                                                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '4px', lineHeight: 1.3 }}>
                                                                            {textoVencimiento}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className="col-comentarios" style={{ padding: '0.85rem 1rem', wordBreak: 'break-word', color: '#6B7280', fontSize: '0.85rem' }}
                                                                    title={tramite.comentario || tramite.comentarios || 'Sin comentarios'}
                                                                >
                                                                    {obtenerUltimoComentario(tramite.comentario || tramite.comentarios)}
                                                                </td>
                                                                <td className="text-center" style={{ padding: '0.85rem 1rem' }}>
                                                                    <button className="btn btn-primary btn-sm"
                                                                        onClick={() => editarTramite(tramite.idTramite)}
                                                                        style={{ borderRadius: '6px', fontWeight: '500', padding: '0.35rem 0.85rem', fontSize: '0.85rem' }}>
                                                                        <i className="bi bi-eye me-1"></i>Ver
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default ListadoTramitesART;
